import { message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AssessmentApiError,
  approveInterviewGuide,
  getAssessment,
  updateAssessment,
} from '@/api/assessments/api';
import { type AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';
import {
  readInterviewContextPackStatus,
  readInterviewGuideMarkdownSession,
  writeInterviewGuideMarkdownSession,
  type InterviewContextPackStatus,
} from '@/utils/assessmentInterviewFlow';
import {
  parseInterviewGuideMarkdown,
  type InterviewGuideReviewModel,
} from '@/utils/interviewGuideReviewParser';

const INTERVIEW_GUIDE_FILENAME = '01-interview-guide.md';

/** Options for the `useInterviewGuide` hook. */
export interface UseInterviewGuideOptions {
  assessmentInstanceId: string;
  accessToken: string | null;
  clientId: string;
  projectId: string;
  onNavigateAssessmentStep: (step: AssessmentFlowStep) => void;
}

/** Full return type of `useInterviewGuide`. */
export interface UseInterviewGuideResult {
  isLoading: boolean;
  isSaving: boolean;
  isApproving: boolean;
  isApproved: boolean;
  isEditOpen: boolean;
  visibleMarkdown: string;
  parsedGuide: InterviewGuideReviewModel;
  hasMarkdown: boolean;
  roleCount: number;
  questionCount: number;
  contextPackStatus: InterviewContextPackStatus | null;
  draftMarkdown: string;
  setDraftMarkdown: (v: string) => void;
  openEditor: () => void;
  closeEditor: () => void;
  saveEdit: (markdown: string) => Promise<void>;
  approve: () => Promise<void>;
}

/**
 * Manages the interview guide review step: loading the approved/draft markdown from the backend,
 * tracking edit state, saving changes, and approving the guide to advance the assessment.
 *
 * @param options.onNavigateAssessmentStep - Called after approval to navigate to the next step.
 * @returns Guide content, parsed structure, edit modal state, and save/approve actions.
 */
export function useInterviewGuide({
  assessmentInstanceId,
  accessToken,
  clientId,
  projectId,
  onNavigateAssessmentStep,
}: UseInterviewGuideOptions): UseInterviewGuideResult {
  const [assessmentMarkdown, setAssessmentMarkdown] = useState<string | null>(null);
  const [isApprovedState, setIsApprovedState] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [draftMarkdown, setDraftMarkdown] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const contextPackStatus = useMemo(
    () => readInterviewContextPackStatus(assessmentInstanceId),
    [assessmentInstanceId],
  );

  const visibleMarkdown = useMemo(() => {
    const fromAssessment = assessmentMarkdown?.trim() ?? '';
    if (fromAssessment.length > 0) {
      return assessmentMarkdown ?? '';
    }
    return readInterviewGuideMarkdownSession(assessmentInstanceId) ?? '';
  }, [assessmentMarkdown, assessmentInstanceId]);

  const parsedGuide = useMemo(
    () => parseInterviewGuideMarkdown(visibleMarkdown, INTERVIEW_GUIDE_FILENAME),
    [visibleMarkdown],
  );

  const roleCount = parsedGuide.roleGuides.length;
  const questionCount = parsedGuide.roleGuides.reduce(
    (total, guide) => total + guide.questions.length,
    0,
  );
  const hasMarkdown = visibleMarkdown.trim().length > 0;

  useEffect(() => {
    if (!accessToken) {
      setIsLoading(false);
      setAssessmentMarkdown(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getAssessment(assessmentInstanceId, accessToken, clientId, projectId)
      .then((data) => {
        if (!cancelled) {
          setAssessmentMarkdown(data.interview_guide_markdown ?? null);
          setIsApprovedState(Boolean(data.interview_guide_approved_at));
        }
      })
      .catch((err) => {
        if (!cancelled && err instanceof AssessmentApiError) {
          setAssessmentMarkdown(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, assessmentInstanceId, clientId, projectId]);

  const openEditor = useCallback(() => {
    setDraftMarkdown(visibleMarkdown);
    setIsEditOpen(true);
  }, [visibleMarkdown]);

  const closeEditor = useCallback(() => {
    setIsEditOpen(false);
  }, []);

  const saveEdit = useCallback(
    async (markdown: string): Promise<void> => {
      setIsSaving(true);
      try {
        if (accessToken) {
          const updated = await updateAssessment(
            assessmentInstanceId,
            { interview_guide_markdown: markdown },
            accessToken,
            clientId,
            projectId,
          );
          setAssessmentMarkdown(updated.interview_guide_markdown ?? markdown);
        } else {
          setAssessmentMarkdown(markdown);
        }
        writeInterviewGuideMarkdownSession(assessmentInstanceId, markdown);
        setIsEditOpen(false);
        void message.success('Interview guide updated');
      } catch {
        void message.error('Unable to save interview guide');
      } finally {
        setIsSaving(false);
      }
    },
    [accessToken, assessmentInstanceId, clientId, projectId],
  );

  const approve = useCallback(async (): Promise<void> => {
    if (!hasMarkdown) {
      return;
    }
    if (accessToken) {
      setIsApproving(true);
      try {
        await approveInterviewGuide(assessmentInstanceId, accessToken, clientId, projectId);
        setIsApprovedState(true);
      } catch {
        void message.error('Unable to approve interview guide');
        return;
      } finally {
        setIsApproving(false);
      }
    }
    onNavigateAssessmentStep(2);
  }, [
    hasMarkdown,
    accessToken,
    assessmentInstanceId,
    clientId,
    projectId,
    onNavigateAssessmentStep,
  ]);

  return {
    isLoading,
    isSaving,
    isApproving,
    isApproved: isApprovedState,
    isEditOpen,
    visibleMarkdown,
    parsedGuide,
    hasMarkdown,
    roleCount,
    questionCount,
    contextPackStatus,
    draftMarkdown,
    setDraftMarkdown,
    openEditor,
    closeEditor,
    saveEdit,
    approve,
  };
}
